/**
 * What the scoped API keys screen decides without needing a component.
 *
 * Svelte-free on purpose, like ./correction.js: the Node test runner imports it
 * directly (test/frontend-api-keys.test.js), so the rules a mistaken screen
 * could get wrong — which day a key expires on, what a replacement key inherits,
 * when an integration label is mandatory — are covered without a browser.
 *
 * Permission ids are the backend's (src/auth/api-key-policy.js). Nothing here
 * invents one: a preset lists ids and is offered only when the server's own
 * catalogue contains every one of them.
 */

const ROME = 'Europe/Rome';

/** The same pattern as the backend and as the CHECK constraint on the table. */
export const INTEGRATION_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

/** Permissions that expose company-wide customer, invoice or XML data. */
export const WIDE_READ_PERMISSIONS = Object.freeze([
  'customers:read', 'invoices:read', 'invoices:export-xml',
]);

/**
 * Ready-made permission sets. The label and the hint live in the Italian
 * catalog under `companySettings.apiKeys.presets.<id>`.
 */
export const PRESETS = Object.freeze([
  Object.freeze({ id: 'draftsOnly', permissions: Object.freeze(['drafts:create', 'drafts:read-own']) }),
]);

export function isValidIntegration(value) {
  return typeof value === 'string' && INTEGRATION_PATTERN.test(value);
}

/** Whether a selection obliges an integration label: any draft permission does. */
export function requiresIntegration(selected) {
  return (selected || []).some((id) => String(id).startsWith('drafts:'));
}

/** Whether a selection includes a permission that reads the whole company's data. */
export function selectsWideRead(selected) {
  return (selected || []).some((id) => WIDE_READ_PERMISSIONS.includes(id));
}

/**
 * The presets the server can actually honour: those whose every permission is
 * in the catalogue it published.
 *
 * @param {string[]} catalogueIds
 */
export function availablePresets(catalogueIds) {
  const known = new Set(catalogueIds || []);
  return PRESETS.filter((preset) => preset.permissions.every((id) => known.has(id)));
}

/**
 * Group permission ids by what they are about — the part before the colon —
 * keeping the order the catalogue gave them in.
 *
 * @param {string[]} ids
 * @returns {Array<{ group: string, ids: string[] }>}
 */
export function groupPermissions(ids) {
  const groups = new Map();
  for (const id of ids || []) {
    const group = String(id).split(':')[0];
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(id);
  }
  return [...groups].map(([group, members]) => ({ group, ids: members }));
}

/** The distinct integration labels of a company's keys, sorted. */
export function integrationLabels(keys) {
  return [...new Set((keys || []).map((key) => key.integration).filter(Boolean))].sort();
}

/** The wall-clock offset of Europe/Rome from UTC at an instant, in milliseconds. */
function romeOffsetMs(instant) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ROME,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const part = (type) => Number(parts.find((p) => p.type === type).value);
  const wallAsUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'));
  return wallAsUtc - Math.floor(instant / 1000) * 1000;
}

/**
 * The instant a key chosen to expire "on" a calendar day stops working: the end
 * of that day in Italy, whatever the browser's own time zone, so that a key
 * expiring on 31 October works until midnight Italian time and not until
 * midnight wherever the administrator happens to be.
 *
 * @param {string} day - `YYYY-MM-DD`, as a date input produces it
 * @returns {string|null} an ISO instant, or null when `day` is not a real date
 */
export function endOfRomeDay(day) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day ?? ''));
  if (!match) return null;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(year, month - 1, date));
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== date) return null;

  const wall = Date.UTC(year, month - 1, date, 23, 59, 59);
  // The offset is a property of the instant, and the instant is what is being
  // looked for, so it is estimated and then re-read once at the estimate. The
  // second pass is what keeps the two days a year the offset changes right.
  const estimate = wall - romeOffsetMs(wall);
  return new Date(wall - romeOffsetMs(estimate)).toISOString();
}

/**
 * The Italian calendar day an instant falls on, as a date input wants it.
 *
 * @param {string|Date} instant
 * @returns {string} `YYYY-MM-DD`
 */
export function romeDay(instant) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: ROME, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(instant));
  const part = (type) => parts.find((p) => p.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * What the form is filled with when a key is replaced.
 *
 * The integration is carried over unchanged and the form shows it read-only:
 * it is what draft ownership and idempotency follow across the rotation, and a
 * typo in a retyped label would quietly start a new namespace. The expiry is
 * carried over only while it is still ahead.
 *
 * @param {object} key - a key as the API lists it
 * @param {number} [now]
 */
export function successorOf(key, now = Date.now()) {
  const stillAhead = key.expiresAt && new Date(key.expiresAt).getTime() > now;
  return {
    name: key.name,
    integration: key.integration ?? '',
    permissions: [...(key.permissions || [])],
    expiryDay: stillAhead ? romeDay(key.expiresAt) : '',
  };
}

/**
 * Whether the create form may be submitted, and the first reason it may not.
 *
 * @param {{ name: string, integration: string, permissions: string[] }} form
 * @returns {{ ok: boolean, reason: null | 'name' | 'permissions' | 'integrationRequired' | 'integrationInvalid' }}
 */
export function validateCreateForm(form) {
  const name = String(form.name ?? '').trim();
  if (name.length < 1 || name.length > 100) return { ok: false, reason: 'name' };
  if (!form.permissions || form.permissions.length === 0) return { ok: false, reason: 'permissions' };
  const integration = String(form.integration ?? '').trim();
  if (integration === '') {
    return requiresIntegration(form.permissions) ? { ok: false, reason: 'integrationRequired' } : { ok: true, reason: null };
  }
  return isValidIntegration(integration) ? { ok: true, reason: null } : { ok: false, reason: 'integrationInvalid' };
}
